// src/components/content/PricingContent.tsx

"use client"

import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloudLine from '~/icons/remix/generated/RiCloudLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiSubtractLine from '~/icons/remix/generated/RiSubtractLine'
import RiUserLine from '~/icons/remix/generated/RiUserLine'
import { Fragment, useRef, useState } from "react"
import Link from "next/link"

import { Badge } from "~/components/Badge"
import { Button } from "~/components/Button"
import { Label } from "~/components/Label"
import { Switch } from "~/components/Switch"
import { Tooltip } from "~/components/Tooltip"
import Testimonial from "~/components/ui/Testimonial"
import { ArrowAnimated } from "~/components/ui/ArrowAnimated"
import { cx } from "~/lib/utils"
import Cta from "~/components/ui/about/cta"
import { trackMarketingCtaClick, trackSiteAction } from "~/lib/gtag"
import { FrameworksPricingBlock } from "~/components/pricing/FrameworksPricingBlock"

import { plans, pricingSections } from "~/app/pricing/plans"
import type { FixedPrice, VariablePrice } from "~/types"

const isVariablePrice = (price: FixedPrice | VariablePrice): price is VariablePrice => {
  return (price as VariablePrice).monthly !== undefined
}

export default function PricingContent(): JSX.Element {
  const [billingFrequency, setBillingFrequency] = useState<"monthly" | "annually">(
    "annually"
  )

  // Local refs for sections
  const pricingOverviewRef = useRef<HTMLDivElement>(null)
  const pricingDetailsRef = useRef<HTMLDivElement>(null)

  const handleBillingFrequencyChange = (newFrequency: "monthly" | "annually"): void => {
    setBillingFrequency(newFrequency)
    trackSiteAction({
      kind: "pricing",
      label: "billing_toggle",
      surface: "pricing",
      element_text: newFrequency,
    })
  }

  const handlePlanCtaClick = (plan: (typeof plans)[number]): void => {
    trackMarketingCtaClick({
      placement: "pricing_tier",
      destinationPath: plan.buttonLink,
      planTier: plan.name,
      linkText: plan.buttonText,
    })
  }

  return (
    <>
      <div className="space-y-3 mx-auto max-w-6xl">
        <section
        aria-labelledby="pricing-title"
        className="animate-slide-up-fade px-4"
        style={{
          animationDuration: "600ms",
          animationFillMode: "backwards"
        }}>
        <Badge>Pricing</Badge>
        <h1 id="pricing-title" className="mt-2 inline-block bg-gradient-to-br from-gray-900 to-gray-800 bg-clip-text py-2 text-4xl font-bold text-transparent sm:text-6xl md:text-6xl dark:from-gray-50 dark:to-gray-300">
          Our plans scale with you
        </h1>
        <p className="mt-6 text-lg tracking-tight text-gray-700 dark:text-gray-400">
          Plans that empower you and your team to scale your ad performance without
          friction. Our flexible pricing models ensure that efficiency doesn&rsquo;t
          come at the cost of your budget.
        </p>
      </section>
      <section
        id="pricing-overview"
        ref={pricingOverviewRef}
        className="mt-32 animate-slide-up-fade max-w-6xl mx-auto px-6"
        aria-labelledby="pricing-overview-title"
        style={{
          animationDuration: "600ms",
          animationDelay: "200ms",
          animationFillMode: "backwards"
        }}>
        <h2 id="pricing-overview-title" className="sr-only">Pricing Overview</h2>
        <div className="flex items-center justify-center gap-2 mb-12">
          <Label
            htmlFor="billing-toggle"
            className="text-base font-medium sm:text-sm dark:text-gray-400">
            Monthly
          </Label>
          <Switch
            id="billing-toggle"
            aria-label={`Switch to ${billingFrequency === "monthly" ? "annual" : "monthly"} billing`}
            checked={billingFrequency === "annually"}
            onCheckedChange={() =>
              handleBillingFrequencyChange(
                billingFrequency === "monthly" ? "annually" : "monthly"
              )
            }
          />
          <Label
            htmlFor="billing-toggle"
            className="text-base font-medium sm:text-sm dark:text-gray-400">
            Yearly (-20%)
          </Label>
        </div>
        <div className="grid grid-cols-1 gap-x-14 gap-y-8 lg:grid-cols-3">
          {plans.map((plan, planIdx) => (
            <div key={planIdx} className="mt-6">
              {plan.isRecommended ? (
                <div className="flex h-4 items-center">
                  <div className="relative w-full">
                    <div
                      className="absolute inset-0 flex items-center"
                      aria-hidden="true">
                      <div className="w-full border-t border-indigo-600 dark:border-indigo-400" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-xs font-medium text-indigo-600 dark:bg-gray-950 dark:text-indigo-400">
                        Most popular
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-4 items-center">
                  <div className="h-px w-full" />
                </div>
              )}
              <div className="mx-auto max-w-md">
                <h3 className="mt-6 text-sm font-semibold text-gray-900 dark:text-gray-50">
                  {plan.name}
                </h3>
                <div className="mt-3 flex items-center gap-x-3">
                  <span className="text-5xl font-semibold tabular-nums text-gray-900 dark:text-gray-50">
                    {isVariablePrice(plan.price)
                      ? billingFrequency === "monthly"
                        ? plan.price.monthly
                        : plan.price.annually
                      : plan.price}
                  </span>
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    per user <br /> per month
                  </div>
                </div>
                <div className="mt-6 flex flex-col justify-between">
                  <p className="text-sm leading-6 text-gray-600 dark:text-gray-400">
                    {plan.description}
                  </p>
                  <div className="mt-6">
                    {plan.isStarter ? (
                      <Button variant="secondary" asChild className="group">
                        <Link href={plan.buttonLink} onClick={() => handlePlanCtaClick(plan)}>
                          {plan.buttonText}
                          <ArrowAnimated />
                        </Link>
                      </Button>
                    ) : (
                      <Button asChild className="group">
                        <Link href={plan.buttonLink} onClick={() => handlePlanCtaClick(plan)}>
                          {plan.buttonText}
                          <ArrowAnimated />
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
                <ul
                  role="list"
                  className="mt-4 text-sm text-gray-700 dark:text-gray-400">
                  {plan.capacity.map((feature, index) => (
                    <li key={feature} className="flex items-center gap-x-3 py-1.5">
                      {index === 0 && (
                        <RiUserLine
                          className="size-4 shrink-0 text-gray-500"
                          aria-hidden="true"
                        />
                      )}
                      {index === 1 && (
                        <RiCloudLine
                          className="size-4 shrink-0 text-gray-500"
                          aria-hidden="true"
                        />
                      )}
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <ul
                  role="list"
                  className="mt-4 text-sm text-gray-700 dark:text-gray-400">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-x-3 py-1.5">
                      <RiCheckLine
                        className="size-4 shrink-0 text-indigo-600 dark:text-indigo-400"
                        aria-hidden="true"
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* plan details (xs-lg)*/}
      <section
        id="pricing-details"
        ref={pricingDetailsRef}
        className="px-6 pt-12 sm:pt-16 md:pt-20"
        aria-labelledby="pricing-details-title">
        <h2 id="pricing-details-title" className="sr-only">Pricing Details</h2>
        <div className="max-w-6xl space-y-8 sm:max-w-6xl lg:hidden">
          {plans.map((plan) => (
            <div key={plan.name}>
              <div className="rounded-xl backdrop-blur-xl p-6 ring-1 ring-inset ring-gray-200 dark:ring-gray-800">
                <h3
                  id={`${plan.name}-details`}
                  className="text-base font-semibold subpixel-antialiased leading-6 text-gray-900 dark:text-gray-100">
                  {plan.name}
                </h3>
                <p className="text-sm font-normal text-gray-600 dark:text-gray-400">
                  {isVariablePrice(plan.price)
                    ? `${
                        billingFrequency === "monthly"
                          ? plan.price.monthly
                          : plan.price.annually
                      } / per user`
                    : plan.price}
                </p>
              </div>
              <ul
                role="list"
                className="md:px-6 sm:px-6 px-2 mt-10 space-y-10 text-sm leading-6 text-gray-900 dark:text-gray-50">
                {pricingSections.map((section) => (
                  <li key={section.name}>
                    <h4 className="font-semibold">{section.name}</h4>
                    <ul
                      role="list"
                      className="mt-2 divide-y divide-gray-200 dark:divide-gray-800">
                      {section.features.map((feature) =>
                        feature.plans[plan.name] ? (
                          <li key={feature.name} className="flex gap-x-3 py-2.5">
                            <RiCheckLine
                              className="size-5 flex-none text-indigo-600 dark:text-indigo-400"
                              aria-hidden="true"
                            />
                            <span>
                              {feature.name}{" "}
                              {typeof feature.plans[plan.name] === "string" ? (
                                <span className="text-sm leading-6 text-gray-600 dark:text-gray-400">
                                  ({feature.plans[plan.name]})
                                </span>
                              ) : null}
                            </span>
                          </li>
                        ) : null
                      )}
                    </ul>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

        {/* plan details (lg+) */}
        <section className="mx-8" aria-labelledby="pricing-comparison-title">
          <h2 id="pricing-comparison-title" className="sr-only">Pricing Comparison</h2>
          <div className="hidden lg:block">
            <div className="relative">
              {/* Solid page-bg mask (NOT translucent + backdrop-blur): its whole job
                  is to occlude rows scrolling behind the fixed pill nav above the
                  sticky thead, and a 90%-alpha band let those rows ghost through
                  while unpinning at the table's end — right above the frameworks
                  section. Solid bg also removes a second backdrop-filter layered
                  beneath the nav's own. */}
              <div className="sticky top-0 z-20 h-28 w-full bg-slate-100 dark:bg-gray-950" />
              <table className="w-full table-fixed border-separate border-spacing-0 text-left">
                <caption className="sr-only">Pricing plan comparison</caption>
                <colgroup>
                  <col className="w-2/5" />
                  <col className="w-1/5" />
                  <col className="w-1/5" />
                  <col className="w-1/5" />
                </colgroup>
                {/* Solid for the same reason as the mask above — dark:bg-gray-950/25
                    left the table's last rows readable through the pinned header at
                    the junction (the reported mid-page "ghost"). */}
                <thead className="sticky top-28 bg-slate-100 dark:bg-gray-950">
                  <tr>
                    <th
                      scope="col"
                      className="border-b border-gray-100 pb-8 dark:border-gray-800/50">
                      <div className="font-semibold leading-7 text-gray-900 dark:text-gray-50">
                        Compare prices
                      </div>
                      <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                        Price per month (billed yearly)
                      </div>
                    </th>
                    {plans.map((plan) => (
                      <th
                        key={plan.name}
                        scope="col"
                        className="border-b border-gray-100 px-6 pb-8 lg:px-8 dark:border-gray-800">
                        <div
                          className={cx(
                            !plan.isStarter
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-gray-900 dark:text-gray-50",
                            "font-semibold leading-7"
                          )}>
                          {plan.name}
                        </div>
                        <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                          {isVariablePrice(plan.price)
                            ? `${
                                billingFrequency === "monthly"
                                  ? plan.price.monthly
                                  : plan.price.annually
                              } / per user`
                            : plan.price}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pricingSections.map((section, sectionIdx) => (
                    <Fragment key={section.name}>
                      <tr>
                        <th
                          scope="colgroup"
                          colSpan={4}
                          className={cx(
                            sectionIdx === 0 ? "pt-8" : "pt-16",
                            "border-b border-gray-100 pb-4 text-base font-semibold leading-6 text-gray-900 dark:border-gray-800 dark:text-gray-50"
                          )}>
                          {section.name}
                        </th>
                      </tr>
                      {section.features.map((feature) => (
                        <tr
                          key={feature.name}
                          className="transition hover:bg-indigo-50/30 dark:hover:bg-indigo-800/5">
                          <th
                            scope="row"
                            className="flex items-center gap-2 border-b border-gray-100 py-4 text-sm font-normal leading-6 text-gray-900 dark:border-gray-800 dark:text-gray-50">
                            <span>{feature.name}</span>
                            {feature.tooltip ? (
                              <Tooltip side="right" content={feature.tooltip}>
                                <RiInformationLine
                                  className="size-4 shrink-0 text-gray-700 dark:text-gray-400"
                                  aria-hidden="true"
                                />
                              </Tooltip>
                            ) : null}
                          </th>
                          {plans.map((plan) => (
                            <td
                              key={plan.name}
                              className="border-b border-gray-100 px-6 py-4 lg:px-8 dark:border-gray-800">
                              {typeof feature.plans[plan.name] === "string" ? (
                                <div className="text-sm leading-6 text-gray-600 dark:text-gray-400">
                                  {feature.plans[plan.name]}
                                </div>
                              ) : (
                                <>
                                  {feature.plans[plan.name] === true ? (
                                    <RiCheckLine
                                      className="h-5 w-5 text-indigo-600 dark:text-indigo-400"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <RiSubtractLine
                                      className="h-5 w-5 text-gray-400 dark:text-gray-600"
                                      aria-hidden="true"
                                    />
                                  )}

                                  <span className="sr-only">
                                    {feature.plans[plan.name] === true
                                      ? "Included"
                                      : "Not included"}{" "}
                                    in {plan.name}
                                  </span>
                                </>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <FrameworksPricingBlock />
        <div className="max-w-screen">
          <Testimonial />
        </div>
        <div className="mb-11 rounded-2xl shadow-md">
          <Cta />

        </div>
      </div>
    </>
  )
}
